import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import StepModalDefaultContent from 'pages/OnboardingFlow/StepModalDefaultContent';
import AddPatientsStep from 'pages/OnboardingFlow/AddPatientsStep';
import MedicationHistoryStep from 'pages/OnboardingFlow/MedicationHistoryStep';
import InformPatientCareStep from 'pages/OnboardingFlow/InformPatientCareStep';
import { Button, Typography } from '@mui/material';
import Theme from 'theme';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

export interface StepModalProps {
  open: boolean;
  step?: number;
  onNext: () => void;
  onClose: () => void;
  totalSteps?: number;
}


export function StepModal({
  open,
  step: stepProp = 0,
  onNext: onNextProp,
  onClose,
  totalSteps = 4,
}: StepModalProps) {
  const [step, setStep] = React.useState(stepProp);
  // Sync internal step state with prop when modal is opened or step prop changes
  React.useEffect(() => {
    if (open) {
      setStep(stepProp);
    }
  }, [open, stepProp]);

  const goBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const stepDescriptions = [
    'default_content',
    'add_patients',
    'medication_history',
    'inform_patient_care',
  ];

  const handleNext = () => {
    const nextStep = step + 1;
    const stepName = stepDescriptions[nextStep] || `step_${nextStep + 1}`;
    if (step === 3) {
      trackMixpanelEvent(MixpanelEventName.ONBOARDING_MODAL_DONE_BUTTON_CLICKED);
      if (onNextProp) onNextProp();
      if (onClose) onClose();
    }
    else {
      trackMixpanelEvent(MixpanelEventName.ONBOARDING_MODAL_NEXT_BUTTON_CLICKED, {
        step: nextStep, stepName,
      });
      setStep(nextStep);
    }
  };

  let content = null;
  if (step === 0) {
    content = <StepModalDefaultContent />;
  }
  else if (step === 1) {
    content = <AddPatientsStep />;
  }
  else if (step === 2) {
    content = <MedicationHistoryStep />;
  }
  else if (step === 3) {
    content = <InformPatientCareStep />;
  }
  else {
    content = null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          width: '100%',
          borderRadius: { xs: 3 },
          background: '#fff',
          boxShadow: '0 3px 5px -1px rgba(0,0,0,0.08), 0 6px 10px 0 rgba(0,0,0,0.07)',
          m: { xs: 1.5 },
        },
      }}
    >
      <DialogContent
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          py: 3,
          position: 'relative',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Box display='flex' alignItems='center' flexDirection='row' gap={1} width='100%' pb={2}>
          {(step === 1 || step === 2 || step === 3) && (
          <IconButton
            onClick={goBack}
            disableRipple
            size='small'
            sx={{ color: Theme.custom.colors.lightTextSecondary, p: 0 }}
          >
            <ArrowBackIosIcon />
            <Typography fontSize={16} variant='body1'>
              BACK
            </Typography>
          </IconButton>
          )}
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{ color: Theme.custom.colors.lightTextPrimary, ml: 'auto', p: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {/* Step content */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {content}
        </Box>
        {/* Dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, gap: 1 }}>
          {Array.from({ length: totalSteps }).map((_, i) => {
            const dotKey = `dot-${i}`;
            return (
              <Box
                key={dotKey}
                width={12}
                height={12}
                borderRadius='50%'
                bgcolor={i === step ? '#3A7EC1' : '#E0E6ED'}
                border={i === step ? '2px solid #3A7EC1' : '2px solid #E0E6ED'}
                boxShadow={i === step ? '0 2px 6px 0 rgba(58,126,193,0.12)' : 'none'}
                sx={{ transition: 'all 0.2s' }}
              />
            );
          })}
        </Box>
        {/* NEXT button below dots */}
        <Button
          variant='contained'
          size='large'
          onClick={handleNext}
          sx={{
            mt: 1,
            fontWeight: 500,
            fontSize: 14,
            borderRadius: 1,
            px: 5,
            background: Theme?.custom?.colors?.lightPrimaryMain,
          }}
        >
          {step === 3 ? 'DONE' : 'NEXT'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
